import React, { useState, useEffect, useMemo, useRef, useCallback } from"react";
import { useParams, useNavigate, Link, useLocation } from"react-router-dom";
import { useTranslation } from"react-i18next";
import {
 DollarSign,
 Calendar,
 User,
 Car,
 ArrowLeft,
 FileText,
 CheckCircle,
 Clock,
 XCircle,
 Percent,
 Receipt,
 Eye,
 Plus,
 Pencil,
 Trash2,
 Wrench,
 History,
 Loader2,
 Download,
 FileDown,
 AlertTriangle,
} from"lucide-react";
import { useAuth } from"../../contexts/AuthContext";
import {
 Card,
 CardHeader,
 CardTitle,
 CardContent,
 ModernButton,
 Badge,
 Toast,
 ToastType,
 Tabs,
 TabsList,
 TabsTrigger,
 TabsContent,
 Table,
 TableHeader,
 TableBody,
 TableRow,
 TableHead,
 TableCell,
 Select,
 SelectContent,
 SelectItem,
 SelectTrigger,
 SelectValue,
 Label,
 Input,
 DetailPageSkeleton,
 ConfirmDialog,
 Accordion,
 AccordionItem,
} from"../../components/ui";
import { apiService, FiscalExportReadinessResponse } from"../../services/api";
import {
 Sale,
 Invoice,
 Commission,
 CommissionRule,
 SaleCost,
 SaleChecklistItem,
 SaleOnboardingProgress,
 CustomerLeadSnapshot,
} from"../../types";
import { formatCurrency, formatDateToBR, parseCurrency, numberToCurrencyInputValue } from"../../utils/formatters";
import {
 resolveCommissionDisplay,
 commissionOriginalPaidAndDiff,
 commissionRuleHasFixedPortion,
 commissionRuleFixedAmount,
 sumCommissionBonuses,
} from"../../utils/commissionCalculation";
import { commissionRuleAppliesToManualSaleAdd } from"../../utils/commissionRuleManualAdd";
import { computeSalePaymentSummary } from"../../utils/salePaymentSummary";
import { DocumentList, DocumentUpload } from"../../components/documents";
import type { EntityDocument } from"../../components/documents/DocumentList";
import {
 SaleOnboardingStepper,
 SaleOnboardingRequirementsCard,
 SaleDocumentationVehicleTab,
 SaleDocumentationChecklistSection,
 SalePostSalesContractsTab,
 SALE_LIFECYCLE_PHASE_ORDER,
 SALE_ONBOARDING_STEP_ORDER,
 stepCompleteForOnboarding,
 TAB_BY_ONBOARDING_STEP,
 getLifecyclePhaseTab,
 isLifecycleOnboardingPhaseComplete,
 CommissionActiveRulesTable,
} from"../../components/financial";
import { buildFallbackOnboardingProgress } from"../../utils/saleOnboardingFallback";
import { mapVehicleRegistryDocumentsToEntityList } from"../../utils/mapVehicleRegistryDocuments";
import { PriceInput } from"../../components/form/PriceInput";
import { SalePaymentsTabContent } from"./SalePaymentsTabContent";
import { cn } from"@/lib/utils";

type CommissionDraft = {
 baseValue: number;
 percentage: number;
 calculatedValue: number;
 fixedValue?: number;
};

type CommissionBonusDraftLine = {
 key: string;
 serverId?: string;
 label: string;
 amount: string;
};

export const SaleDetailsPage: React.FC = () => {
 const { t } = useTranslation();
 const { id } = useParams<{ id: string }>();
 const navigate = useNavigate();
 const location = useLocation();
 const isPostSalesWizard = location.pathname.includes("/post-sales/pipeline/");
 const { user } = useAuth();
 const [sale, setSale] = useState<Sale | null>(null);
 const [isLoading, setIsLoading] = useState(true);
 const [toast, setToast] = useState<{
 message: string;
 type: ToastType;
 } | null>(null);
 const [saleDocuments, setSaleDocuments] = useState<EntityDocument[]>([]);
 const [vehicleRegistryDocuments, setVehicleRegistryDocuments] = useState<EntityDocument[]>([]);
 const [addCommissionDialogOpen, setAddCommissionDialogOpen] = useState(false);
 const [availableRules, setAvailableRules] = useState<CommissionRule[]>([]);
 const [selectedRuleId, setSelectedRuleId] = useState<string>("");
 const [isCreatingCommission, setIsCreatingCommission] = useState(false);
 const [showAddCost, setShowAddCost] = useState(false);
 const [newCostDescription, setNewCostDescription] = useState("");
 const [newCostAmount, setNewCostAmount] = useState("");
 const [newCostType, setNewCostType] = useState("");
 const [isAddingCost, setIsAddingCost] = useState(false);
 const [editingCommissionId, setEditingCommissionId] = useState<string | null>(null);
 const [editCommissionBase, setEditCommissionBase] = useState("");
 const [editCommissionPercent, setEditCommissionPercent] = useState("");
 const [editCommissionCalculated, setEditCommissionCalculated] = useState("");
 const [editCommissionFixed, setEditCommissionFixed] = useState("");
const [commissionDrafts, setCommissionDrafts] = useState<Record<string, CommissionDraft>>({});
const [isCommissionsEditingUnlocked, setIsCommissionsEditingUnlocked] = useState(false);
 const [extraCommissionRulesById, setExtraCommissionRulesById] = useState<
 Record<string, CommissionRule>
 >({});
 const [isSavingCommissionEdit, setIsSavingCommissionEdit] = useState(false);
 const [commissionIdPendingDelete, setCommissionIdPendingDelete] = useState<string | null>(null);
 const [isDeletingCommission, setIsDeletingCommission] = useState(false);
 const [isConfirmingCommissionsStep, setIsConfirmingCommissionsStep] = useState(false);
 const [markingCommissionPaidId, setMarkingCommissionPaidId] = useState<string | null>(null);
 const [editPanelBonusDraft, setEditPanelBonusDraft] = useState({ label: "", amount: "" });
 const [editCommissionBonusesDraft, setEditCommissionBonusesDraft] = useState<
 CommissionBonusDraftLine[]
 >([]);
 const bonusEditBaselineRef = useRef<Map<string, { label: string; amount: number }>>(new Map());
 const [activeCommissionRulesCatalog, setActiveCommissionRulesCatalog] = useState<CommissionRule[]>([]);
 const [applyStoreRulesToAllSales, setApplyStoreRulesToAllSales] = useState(true);
 const [timelineEvents, setTimelineEvents] = useState<
 Array<{ occurredAt: string; type: string; title: string; payload: Record<string, unknown> }>
 >([]);
 const [timelineLoading, setTimelineLoading] = useState(false);
 const [savingChecklistId, setSavingChecklistId] = useState<string | null>(null);
 const [detailsTab, setDetailsTab] = useState("details");
 const [onboardingProgress, setOnboardingProgress] = useState<SaleOnboardingProgress | null>(null);
 const [onboardingLoading, setOnboardingLoading] = useState(false);
 const [sendingPipeline, setSendingPipeline] = useState(false);
 const [customerDocuments, setCustomerDocuments] = useState<EntityDocument[]>([]);
 const [fiscalReadiness, setFiscalReadiness] = useState<FiscalExportReadinessResponse | null>(null);
 const [fiscalPdfLoading, setFiscalPdfLoading] = useState(false);
 const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
 const [wizardStepIndex, setWizardStepIndex] = useState(0);
 const [wizardStepPersisting, setWizardStepPersisting] = useState<
 "idle" | "forward" | "backward"
 >("idle");
 const onboardingStepInitRef = useRef<string | null>(null);

const canMarkAsPaid =
  user?.dealershipRole ==="owner"||
  user?.dealershipRole ==="manager"||
  user?.dealershipRole ==="financial";
const isFromPostSales = location.pathname.startsWith('/post-sales/pipeline');
const isSentToPreparation = sale?.operationalStage !== "pre_sale" && !isFromPostSales;
const canMutateCommissions = Boolean(
 canMarkAsPaid &&
 !isSentToPreparation &&
 (!sale?.commissionsConfirmedAt || isCommissionsEditingUnlocked)
);

 const canMarkCommissionAsPaid = Boolean(
 canMarkAsPaid && !isSentToPreparation && Boolean(sale?.commissionsConfirmedAt)
 );

 const canMutateOnboarding = Boolean(
 user?.dealershipRole && user.dealershipRole !=="viewer"
 );

 const paymentTotals = useMemo(
 () => (sale ? computeSalePaymentSummary(sale) : null),
 [sale]
 );

 const saleCommissionEvalContext = useMemo(() => {
 if (!sale) return null;
 const vehicleType =
 (sale.vehicle as { condition?: string } | undefined)?.condition ==="new"?"new":"used";
 const paymentFormIds = Array.from(
 new Set(
 ((sale.proposal as { payments?: Array<{ paymentFormId?: string }> } | undefined)?.payments || [])
 .map((p) => p.paymentFormId)
 .filter(Boolean)
 )
 ) as string[];
 return {
 vehicleType,
 paymentFormIds,
 saleSalespersonId: sale.salespersonId,
 applyStoreRulesToAllSales,
 };
 }, [sale, applyStoreRulesToAllSales]);

 const sortedActiveCommissionRules = useMemo(() => {
 return [...activeCommissionRulesCatalog].sort((a, b) => {
 if (Boolean(a.isMandatory) !== Boolean(b.isMandatory)) {
 return a.isMandatory ? -1 : 1;
 }
 return a.name.localeCompare(b.name);
 });
 }, [activeCommissionRulesCatalog]);

 useEffect(() => {
 setExtraCommissionRulesById({});
 }, [sale?.id]);

 useEffect(() => {
 if (!sale?.commissions?.length) return;
 let cancelled = false;
 void (async () => {
 for (const rid of Array.from(new Set(sale.commissions!.map((c) => c.ruleId)))) {
 if (cancelled) return;
 const hasEmbedded = sale.commissions!.some((c) => c.rule?.id === rid);
 const inCatalog = sortedActiveCommissionRules.some((r) => r.id === rid);
 if (hasEmbedded || inCatalog) continue;
 try {
 const raw = await apiService.getCommissionRuleById(rid);
 const rule =
 (raw as { data?: CommissionRule }).data ?? (raw as CommissionRule);
 if (!cancelled && rule && typeof rule === "object" && "id" in rule) {
 setExtraCommissionRulesById((prev) => (prev[rid] ? prev : { ...prev, [rid]: rule }));
 }
 } catch {}
 }
 })();
 return () => {
 cancelled = true;
 };
 }, [sale?.id, sale?.commissions, sortedActiveCommissionRules]);

 const isCommissionsBackendBusy = useMemo(
 () =>
 Boolean(
 markingCommissionPaidId ||
 isCreatingCommission ||
 isSavingCommissionEdit ||
 isDeletingCommission ||
 isConfirmingCommissionsStep
 ),
 [
 markingCommissionPaidId,
 isCreatingCommission,
 isSavingCommissionEdit,
 isDeletingCommission,
 isConfirmingCommissionsStep,
 ]
 );

 const resolveCommissionFallbackRule = useCallback(
 (commission: Commission) =>
 commission.rule ??
 sortedActiveCommissionRules.find((r) => r.id === commission.ruleId) ??
 (commission.ruleId ? extraCommissionRulesById[commission.ruleId] : undefined) ??
 null,
 [sortedActiveCommissionRules, extraCommissionRulesById]
 );

const visibleCommissions = useMemo(
 () =>
 (sale?.commissions ?? []).filter((commission) => {
 const ruleMeta = commission.rule ?? resolveCommissionFallbackRule(commission);
 return Boolean(ruleMeta?.isActive);
 }),
 [sale?.commissions, resolveCommissionFallbackRule]
);

useEffect(() => {
 if (!sale?.commissions?.length) {
 setCommissionDrafts({});
 return;
 }
 setCommissionDrafts((prev) => {
 const next: Record<string, CommissionDraft> = {};
 for (const commission of visibleCommissions) {
 const fallbackRule = resolveCommissionFallbackRule(commission);
 const display = resolveCommissionDisplay(commission, sale, fallbackRule);
 next[commission.id] = prev[commission.id] ?? {
 baseValue: display.baseValue,
 percentage: display.percentage,
 calculatedValue: display.calculatedValue,
 ...(commissionRuleHasFixedPortion(fallbackRule)
 ? { fixedValue: commissionRuleFixedAmount(fallbackRule) }
 : {}),
 };
 }
 return next;
 });
}, [visibleCommissions, sale, resolveCommissionFallbackRule]);

useEffect(() => {
 if (!sale?.commissionsConfirmedAt) {
 setIsCommissionsEditingUnlocked(false);
 }
}, [sale?.commissionsConfirmedAt]);

const commissionTotals = useMemo(() => {
 const vehicleValue = parseFloat(String(sale?.finalValue ?? 0)) || 0;
 let commissionsTotal = 0;
 for (const commission of visibleCommissions) {
 const draft = commissionDrafts[commission.id];
 const base =
 draft?.calculatedValue ?? (parseFloat(String(commission.calculatedValue ?? 0)) || 0);
 commissionsTotal += base + sumCommissionBonuses(commission);
 }
 return {
 vehicleValue,
 commissionsTotal,
 vehicleMinusCommissions: vehicleValue - commissionsTotal,
 };
}, [sale?.finalValue, visibleCommissions, commissionDrafts]);

 const leadSnapshot = useMemo((): CustomerLeadSnapshot | null => {
 const raw = sale?.customer?.leadSnapshot;
 if (raw == null || typeof raw !== "object") return null;
 return raw as CustomerLeadSnapshot;
 }, [sale?.customer?.leadSnapshot]);

 const leadSnapshotValidationMessages = useMemo(() => {
 if (!sale) return [];
 const msgs: string[] = [];
 const vid = sale.vehicle?.id;
 const interests = leadSnapshot?.interests;
 if (vid && interests?.length) {
 const ids = new Set(interests.map((i) => i.vehicleId));
 if (!ids.has(vid)) {
 msgs.push(t("financialSales.leadSnapshotValidation.vehicleNotInInterests"));
 }
 }
 const snapDoc = leadSnapshot?.leadCore?.document?.replace(/\D/g, "") ?? "";
 const custDoc = sale.customer?.document?.replace(/\D/g, "") ?? "";
 if (snapDoc && custDoc && snapDoc !== custDoc) {
 msgs.push(t("financialSales.leadSnapshotValidation.documentMismatch"));
 }
 const propVid = sale.proposal?.vehicleId;
 if (propVid && vid && propVid !== vid) {
 msgs.push(t("financialSales.leadSnapshotValidation.proposalVehicleMismatch"));
 }
 return msgs;
 }, [sale, leadSnapshot, t]);

 const editingCommissionForPanel = useMemo(
 () => visibleCommissions.find((c) => c.id === editingCommissionId) ?? null,
 [visibleCommissions, editingCommissionId]
 );

 const editingRuleForPanel = useMemo(
 () =>
 editingCommissionForPanel
 ? resolveCommissionFallbackRule(editingCommissionForPanel)
 : null,
 [editingCommissionForPanel, resolveCommissionFallbackRule]
 );

 const computedEditCommissionTotal = useMemo(() => {
 if (!editingRuleForPanel) return null;
 if (editingRuleForPanel.commissionType === "mixed") {
 const base = parseCurrency(editCommissionBase);
 const pct = parseFloat(editCommissionPercent.replace(",", "."));
 const fx = parseCurrency(editCommissionFixed);
 if (Number.isNaN(base) || Number.isNaN(pct) || Number.isNaN(fx)) return null;
 return (base * pct) / 100 + fx;
 }
 if (editingRuleForPanel.commissionType === "fixed") {
 const fx = parseCurrency(editCommissionFixed);
 if (Number.isNaN(fx)) return null;
 return fx;
 }
 return null;
 }, [editingRuleForPanel, editCommissionBase, editCommissionPercent, editCommissionFixed]);

 const displayOnboardingProgress = useMemo((): SaleOnboardingProgress | null => {
 if (!sale || sale.status ==="cancelled") return null;
 return onboardingProgress ?? buildFallbackOnboardingProgress(sale);
 }, [sale, onboardingProgress]);

const wizardCanAdvance = useMemo(() => {
  if (!isPostSalesWizard || !displayOnboardingProgress) return false;
  if (wizardStepIndex >= 4) return displayOnboardingProgress.canSend;
  const key = SALE_ONBOARDING_STEP_ORDER[wizardStepIndex];
  const currentStepComplete = stepCompleteForOnboarding(displayOnboardingProgress, key);
  if (wizardStepIndex === 1) {
    return displayOnboardingProgress.documentation.complete;
  }
  return currentStepComplete;
}, [isPostSalesWizard, displayOnboardingProgress, wizardStepIndex]);

 useEffect(() => {
 if (id) {
 fetchSale();
 }
 }, [id]);

 useEffect(() => {
 onboardingStepInitRef.current = null;
 }, [id]);

 useEffect(() => {
 if (!isPostSalesWizard || !sale?.id) return;
 if (sale.postSalesOnboardingStepIndex != null) {
 const v = Math.min(4, Math.max(0, Math.floor(sale.postSalesOnboardingStepIndex)));
 setWizardStepIndex(v);
 return;
 }
 if (!displayOnboardingProgress) return;
 if (onboardingStepInitRef.current === sale.id) return;
 onboardingStepInitRef.current = sale.id;
 const ix = SALE_ONBOARDING_STEP_ORDER.findIndex(
 (k) => !stepCompleteForOnboarding(displayOnboardingProgress, k)
 );
 setWizardStepIndex(ix === -1 ? 4 : ix);
 }, [isPostSalesWizard, sale?.id, sale?.postSalesOnboardingStepIndex, displayOnboardingProgress]);

 useEffect(() => {
 if (!isPostSalesWizard || !sale?.id) return;
 const tab = TAB_BY_ONBOARDING_STEP[SALE_ONBOARDING_STEP_ORDER[wizardStepIndex]];
 setDetailsTab(tab);
 }, [isPostSalesWizard, wizardStepIndex, sale?.id]);

 useEffect(() => {
 if (addCommissionDialogOpen && sale) {
 fetchAvailableCommissionRules();
 }
 }, [addCommissionDialogOpen, sale]);

 const refreshOnboarding = useCallback(async (saleId: string) => {
 try {
 setOnboardingLoading(true);
 const raw = await apiService.getSaleOnboardingProgress(saleId);
 const payload =
 (raw as { data?: SaleOnboardingProgress }).data ?? (raw as SaleOnboardingProgress);
 setOnboardingProgress(payload);
 const saleRaw = await apiService.getSaleById(saleId);
 const sp = (saleRaw as { data?: Sale }).data ?? (saleRaw as Sale);
 setSale((prev) =>
 prev?.id === sp.id
 ? {
 ...prev,
 administrativeStage: sp.administrativeStage,
 postSalesOnboardingStepIndex: sp.postSalesOnboardingStepIndex,
 preparationStage: sp.preparationStage,
 status: sp.status,
 operationalStage: sp.operationalStage,
 operationalProgress: sp.operationalProgress,
 checklistItems: sp.checklistItems ?? prev.checklistItems,
 commissionsConfirmedAt: sp.commissionsConfirmedAt ?? prev.commissionsConfirmedAt,
 commissions: sp.commissions ?? prev.commissions,
 }
 : prev
 );
 } catch {
 setOnboardingProgress(null);
 } finally {
 setOnboardingLoading(false);
 }
 }, []);

 const persistPostSalesWizardStep = useCallback(
 async (nextIndex: number) => {
 if (!sale?.id) return;
 const direction = nextIndex > wizardStepIndex ? "forward" : "backward";
 setWizardStepPersisting(direction);
 try {
 await apiService.updateSale(sale.id, { postSalesOnboardingStepIndex: nextIndex });
 setWizardStepIndex(nextIndex);
 await refreshOnboarding(sale.id);
 } catch (err: unknown) {
 const message =
 err && typeof err === "object" && "message" in err && typeof (err as { message: unknown }).message === "string"
 ? (err as { message: string }).message
 : t("postSalesPipeline.wizard.persistStepError");
 setToast({ message, type: "error" });
 } finally {
 setWizardStepPersisting("idle");
 }
 },
 [sale?.id, wizardStepIndex, refreshOnboarding, t]
 );

 useEffect(() => {
 if (!sale?.id || sale.status === "cancelled") return;
 void refreshOnboarding(sale.id);
 }, [sale?.id, sale?.status, refreshOnboarding]);

 const fetchDocuments = async () => {
 if (!sale?.id) return;
 try {
 setIsLoadingDocuments(true);
 const customerDocsPromise = sale.customerId
 ? apiService.getEntityDocuments("customer", sale.customerId).catch(() => [])
 : Promise.resolve([]);
 const [saleDocsResponse, vehicleCadastroResponse, customerDocsResponse] = await Promise.all([
 apiService.getEntityDocuments("sale", sale.id).catch(() => []),
 sale.vehicleId
 ? apiService.getVehicleDocuments(sale.vehicleId).catch(() => [])
 : Promise.resolve([]),
 customerDocsPromise,
 ]);
 const saleDocs = Array.isArray(saleDocsResponse) ? saleDocsResponse : [];
 const customerDocs = Array.isArray(customerDocsResponse) ? customerDocsResponse : [];
 setSaleDocuments(saleDocs as EntityDocument[]);
 setVehicleRegistryDocuments(
 mapVehicleRegistryDocumentsToEntityList(vehicleCadastroResponse),
 );
 setCustomerDocuments(customerDocs as EntityDocument[]);
 } catch (err) {
 console.error("Erro ao carregar documentos:", err);
 } finally {
 setIsLoadingDocuments(false);
 }
 };

 useEffect(() => {
 if (sale?.id) {
 fetchDocuments();
 }
 }, [sale?.id]);

 type TimelineEv = { occurredAt: string; type: string; title: string; payload: Record<string, unknown> };

 const fetchTimelineForSale = async (saleId: string) => {
 try {
 setTimelineLoading(true);
 const raw = await apiService.getSaleTimeline(saleId);
 const events =
 (raw as { data?: { events: TimelineEv[] } })?.data?.events ??
 (raw as { events?: TimelineEv[] })?.events ??
 [];
 setTimelineEvents(events);
 } catch {
 setTimelineEvents([]);
 } finally {
 setTimelineLoading(false);
 }
 };

 const loadCommissionRulesCatalog = useCallback(async () => {
 try {
 const [rules, me] = await Promise.all([
 apiService.getCommissionRules(),
 apiService.getDealershipMe().catch(() => null),
 ]);
 setActiveCommissionRulesCatalog((rules || []).filter((r: any) => r.isActive));
 if (me) {
 setApplyStoreRulesToAllSales(me.commissionApplyStoreRulesToAllSales !== false);
 }
 } catch {
 setActiveCommissionRulesCatalog([]);
 }
 }, []);

 const loadFiscalReadiness = useCallback(async (saleId: string) => {
 try {
 const r = await apiService.getSaleFiscalExportReadiness(saleId);
 setFiscalReadiness(r);
 } catch {
 setFiscalReadiness(null);
 }
 }, []);

 const handleDownloadFiscalJson = async () => {
 if (!sale?.id) return;
 try {
 const data = await apiService.getSaleFiscalExport(sale.id);
 const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
 const url = URL.createObjectURL(blob);
 const a = document.createElement("a");
 a.href = url;
 a.download = `fiscal-export-${sale.id}.json`;
 a.click();
 URL.revokeObjectURL(url);
 } catch {
 setToast({ message: t("financialSales.fiscalExport.loadError"), type: "error" });
 }
 };

 const handleDownloadFiscalPdf = async () => {
 if (!sale?.id) return;
 setFiscalPdfLoading(true);
 try {
 const blob = await apiService.getSaleFiscalExportPdfBlob(sale.id);
 const url = URL.createObjectURL(blob);
 const a = document.createElement("a");
 a.href = url;
 a.download = `fiscal-export-${sale.id}.pdf`;
 a.click();
 URL.revokeObjectURL(url);
 } catch {
 setToast({ message: t("financialSales.fiscalExport.pdfLoadError"), type: "error" });
 } finally {
 setFiscalPdfLoading(false);
 }
 };

 const fetchSale = async () => {
 try {
 setIsLoading(true);
 const data = await apiService.getSaleById(id!);
 const payload = (data as { data?: Sale }).data ?? (data as Sale);
 setSale(payload);
 if (payload?.id) {
 void loadFiscalReadiness(payload.id);
 void fetchTimelineForSale(payload.id);
 void loadCommissionRulesCatalog();
 if (payload.status === "cancelled") {
 setOnboardingProgress(null);
 }
 }
 } catch (err: any) {
 console.error("Erro ao carregar venda:", err);
 setToast({
 message: err.message ||"Erro ao carregar venda",
 type:"error",
 });
 } finally {
 setIsLoading(false);
 }
 };

 const handleChecklistToggle = async (item: SaleChecklistItem, completed: boolean) => {
 if (!sale) return;
 try {
 setSavingChecklistId(item.id);
 await apiService.patchSaleChecklistItem(sale.id, item.id, { completed });
 setToast({ message: t("operation.saleDetail.checklistSaved"), type:"success" });
 await fetchSale();
 } catch (err: unknown) {
 const msg = err instanceof Error ? err.message : t("operation.saleDetail.checklistError");
 setToast({ message: msg, type:"error" });
 } finally {
 setSavingChecklistId(null);
 }
 };

 const getStatusIcon = (status: Sale["status"]) => {
 switch (status) {
 case"confirmed":
 case"paid":
 case"delivered":
 return <CheckCircle className="h-5 w-5 text-green-600"/>;
 case"pending":
 case"open":
 case"waiting_payment":
 return <Clock className="h-5 w-5 text-yellow-600"/>;
 case"in_preparation":
 case"ready_for_delivery":
 return <Clock className="h-5 w-5 text-blue-600"/>;
 case"cancelled":
 return <XCircle className="h-5 w-5 text-red-600"/>;
 default:
 return <Clock className="h-5 w-5 text-muted-foreground"/>;
 }
 };

 const getStatusLabel = (status: Sale["status"]) => {
 const labels: Record<Sale["status"], string> = {
 open:"Em Aberto",
 waiting_payment:"Aguardando Pagamento",
 paid:"Pago",
 in_preparation:"Em Preparação",
 ready_for_delivery:"Pronto para Entrega",
 delivered:"Entregue",
 pending:"Pendente",
 confirmed:"Confirmada",
 cancelled:"Cancelada",
 };
 return labels[status] || status;
 };

 const getStatusColor = (status: Sale["status"]) => {
 switch (status) {
 case"confirmed":
 case"paid":
 case"delivered":
 return"bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
 case"pending":
 case"open":
 return"bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
 case"waiting_payment":
 return"bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200";
 case"in_preparation":
 case"ready_for_delivery":
 return"bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
 case"cancelled":
 return"bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
 default:
 return"bg-surface text-foreground dark:text-muted-foreground";
 }
 };

 const fetchAvailableCommissionRules = async () => {
 if (!sale) return;
 try {
 const allRules = await apiService.getCommissionRules();
 const vehicleType =
 (sale.vehicle as any)?.condition ==="new"?"new":"used";
 const paymentFormIds = Array.from(
 new Set(
 ((sale.proposal as any)?.payments || [])
 .map((p: { paymentFormId?: string }) => p.paymentFormId)
 .filter(Boolean)
 )
 ) as string[];

 const activeOnly = (allRules || []).filter((r: CommissionRule) => r.isActive);

 const applicableRules = activeOnly.filter((rule: CommissionRule) => {
 if (
 !commissionRuleAppliesToManualSaleAdd(rule, {
 vehicleType,
 paymentFormIds,
 saleSalespersonId: sale.salespersonId,
 applyStoreRulesToAllSales,
 })
 ) {
 return false;
 }
 const existingCommission = sale.commissions?.find(
 (c: Commission) => c.ruleId === rule.id
 );
 return !existingCommission;
 });

 setAvailableRules(applicableRules);
 } catch (err: any) {
 console.error("Erro ao carregar regras de comissão:", err);
 setToast({
 message: err.message ||"Erro ao carregar regras de comissão",
 type:"error",
 });
 }
 };

 const handleAddCommission = async () => {
 if (!selectedRuleId || !sale) return;

 try {
 setIsCreatingCommission(true);
 await apiService.createCommission({
 saleId: sale.id,
 ruleId: selectedRuleId,
 });

 setToast({
 message:"Comissão adicionada com sucesso",
 type:"success",
 });

 setAddCommissionDialogOpen(false);
 setSelectedRuleId("");

 await fetchSale();
 if (sale?.id) await refreshOnboarding(sale.id);
 } catch (err: any) {
 console.error("Erro ao adicionar comissão:", err);
 setToast({
 message: err.message ||"Erro ao adicionar comissão",
 type:"error",
 });
 } finally {
 setIsCreatingCommission(false);
 }
 };

 const handleMarkAsPaid = async (commissionId: string) => {
 if (!sale?.commissionsConfirmedAt) {
 setToast({
 message: t("financialSales.commissions.payRequiresCommissionConfirmation"),
 type:"error",
 });
 return;
 }
 try {
 setMarkingCommissionPaidId(commissionId);
 const draft = commissionDrafts[commissionId];
 if (draft) {
 await apiService.updateCommission(commissionId, {
 baseValue: draft.baseValue,
 percentage: draft.percentage,
 calculatedValue: draft.calculatedValue,
 });
 }
 await apiService.markCommissionAsPaid(commissionId);
 setCommissionDrafts((prev) => {
 const next = { ...prev };
 delete next[commissionId];
 return next;
 });
 setToast({
 message:"Comissão marcada como paga com sucesso",
 type:"success",
 });
 await fetchSale();
 if (sale?.id) await refreshOnboarding(sale.id);
 } catch (err: any) {
 console.error("Erro ao marcar comissão como paga:", err);
 setToast({
 message: err.message ||"Erro ao marcar comissão como paga",
 type:"error",
 });
 } finally {
 setMarkingCommissionPaidId(null);
 }
 };

 const beginEditCommission = (commission: Commission) => {
if (!canMutateCommissions) {
setToast({ message: t("financialSales.commissions.lockedAfterPreparation"), type:"error" });
return;
}
 if (commission.status !=="pending") {
 setToast({ message: t("financialSales.commissions.onlyPending"), type:"error" });
 return;
 }
 setEditingCommissionId(commission.id);
 const fallbackRule = resolveCommissionFallbackRule(commission);
 const editDisplay = resolveCommissionDisplay(commission, sale, fallbackRule);
const draft = commissionDrafts[commission.id];
setEditCommissionBase(numberToCurrencyInputValue(draft?.baseValue ?? editDisplay.baseValue));
setEditCommissionPercent((draft?.percentage ?? editDisplay.percentage).toString());
 setEditCommissionCalculated(
numberToCurrencyInputValue(draft?.calculatedValue ?? editDisplay.calculatedValue)
 );
 if (fallbackRule && commissionRuleHasFixedPortion(fallbackRule)) {
 setEditCommissionFixed(
 numberToCurrencyInputValue(
 draft?.fixedValue ?? commissionRuleFixedAmount(fallbackRule)
 )
 );
 } else {
 setEditCommissionFixed("");
 }
 setEditPanelBonusDraft({ label: "", amount: "" });
 const baseline = new Map<string, { label: string; amount: number }>();
 setEditCommissionBonusesDraft(
 (commission.bonuses ?? []).map((b) => {
 const amt = parseFloat(String(b.amount));
 baseline.set(b.id, { label: b.label, amount: amt });
 return {
 key: b.id,
 serverId: b.id,
 label: b.label,
 amount: numberToCurrencyInputValue(amt),
 };
 })
 );
 bonusEditBaselineRef.current = baseline;
 };

 const cancelEditCommission = () => {
 setEditingCommissionId(null);
 setEditCommissionBase("");
 setEditCommissionPercent("");
 setEditCommissionCalculated("");
 setEditCommissionFixed("");
 setEditPanelBonusDraft({ label: "", amount: "" });
 setEditCommissionBonusesDraft([]);
 bonusEditBaselineRef.current = new Map();
 };

 const handleSaveCommissionEdit = async () => {
 if (!editingCommissionId || !sale) return;
 const commissionRow = sale.commissions?.find((c) => c.id === editingCommissionId);
 const rule = commissionRow ? resolveCommissionFallbackRule(commissionRow) : null;
 const baseValue = parseCurrency(editCommissionBase);
 const percentage = parseFloat(editCommissionPercent.replace(",","."));
 const parsedFixed = parseCurrency(editCommissionFixed);
 let calculatedValue = parseCurrency(editCommissionCalculated);
 if (rule?.commissionType === "mixed") {
 if (
 Number.isNaN(baseValue) ||
 Number.isNaN(percentage) ||
 Number.isNaN(parsedFixed) ||
 baseValue < 0 ||
 percentage < 0 ||
 parsedFixed < 0
 ) {
 setToast({
 message: t("financialSales.commissions.invalidValues"),
 type:"error",
 });
 return;
 }
 calculatedValue = (baseValue * percentage) / 100 + parsedFixed;
 } else if (rule?.commissionType === "fixed") {
 if (Number.isNaN(parsedFixed) || parsedFixed < 0) {
 setToast({
 message: t("financialSales.commissions.invalidValues"),
 type:"error",
 });
 return;
 }
 calculatedValue = parsedFixed;
 } else if (
 Number.isNaN(baseValue) ||
 Number.isNaN(percentage) ||
 Number.isNaN(calculatedValue) ||
 baseValue < 0 ||
 percentage < 0 ||
 calculatedValue < 0
 ) {
 setToast({
 message: t("financialSales.commissions.invalidValues"),
 type:"error",
 });
 return;
 }
 for (const row of editCommissionBonusesDraft) {
 const amt = parseCurrency(row.amount);
 if (
 !row.label.trim() ||
 Number.isNaN(amt) ||
 amt < 0
 ) {
 setToast({
 message: t("financialSales.commissions.bonusInvalid"),
 type: "error",
 });
 return;
 }
 }
 setIsSavingCommissionEdit(true);
 try {
 const baseline = bonusEditBaselineRef.current;
 const draftPersistedIds = new Set(
 editCommissionBonusesDraft.filter((r) => r.serverId).map((r) => r.serverId!)
 );
 for (const id of baseline.keys()) {
 if (!draftPersistedIds.has(id)) {
 await apiService.deleteCommissionBonus(editingCommissionId, id);
 }
 }
 for (const row of editCommissionBonusesDraft) {
 const amt = parseCurrency(row.amount);
 if (!row.serverId) {
 await apiService.createCommissionBonus(editingCommissionId, {
 label: row.label.trim(),
 amount: amt,
 });
 } else {
 const prev = baseline.get(row.serverId);
 if (
 prev &&
 (prev.label !== row.label.trim() || prev.amount !== amt)
 ) {
 await apiService.updateCommissionBonus(editingCommissionId, row.serverId, {
 label: row.label.trim(),
 amount: amt,
 });
 }
 }
 }
 setCommissionDrafts((prev) => ({
 ...prev,
 [editingCommissionId]: {
 baseValue,
 percentage,
 calculatedValue,
 ...(rule && commissionRuleHasFixedPortion(rule) ? { fixedValue: parsedFixed } : {}),
 },
 }));
 setToast({
 message: t("financialSales.commissions.saveCommissionEditSuccess"),
 type: "success",
 });
 cancelEditCommission();
 await fetchSale();
 if (sale?.id) await refreshOnboarding(sale.id);
 } catch (err: unknown) {
 const msg =
 err instanceof Error ? err.message : t("financialSales.commissions.bonusSaveError");
 setToast({ message: msg, type: "error" });
 } finally {
 setIsSavingCommissionEdit(false);
 }
 };

 const handleConfirmCommissionsStep = async () => {
 if (!sale?.id) return;
 try {
 setIsConfirmingCommissionsStep(true);
 await apiService.confirmSaleCommissionsOnboardingStep(sale.id);
 setToast({ message: t("financialSales.commissions.confirmStepSuccess"), type: "success" });
 await fetchSale();
 await refreshOnboarding(sale.id);
 } catch (err: unknown) {
 const msg =
 err instanceof Error ? err.message : t("financialSales.commissions.confirmStepError");
 setToast({ message: msg, type: "error" });
 } finally {
 setIsConfirmingCommissionsStep(false);
 }
 };

 const handleDeleteCommissionConfirmed = async () => {
 if (!commissionIdPendingDelete || !sale?.id) return;
 try {
 setIsDeletingCommission(true);
 await apiService.deleteCommission(commissionIdPendingDelete);
 setToast({ message: t("financialSales.commissions.deleteSuccess"), type: "success" });
 setCommissionIdPendingDelete(null);
 await fetchSale();
 await refreshOnboarding(sale.id);
 } catch (err: unknown) {
 const msg =
 err instanceof Error ? err.message : t("financialSales.commissions.deleteError");
 setToast({ message: msg, type: "error" });
 } finally {
 setIsDeletingCommission(false);
 }
 };

 const handleAddCommissionBonusDraftLine = () => {
 if (!editingCommissionId) return;
 const amount = parseCurrency(editPanelBonusDraft.amount);
 if (!editPanelBonusDraft.label.trim() || Number.isNaN(amount) || amount < 0) {
 setToast({
 message: t("financialSales.commissions.bonusInvalid"),
 type: "error",
 });
 return;
 }
 setEditCommissionBonusesDraft((prev) => [
 ...prev,
 {
 key: `new-${crypto.randomUUID()}`,
 label: editPanelBonusDraft.label.trim(),
 amount: numberToCurrencyInputValue(amount),
 },
 ]);
 setEditPanelBonusDraft({ label: "", amount: "" });
 };

 const handleRemoveCommissionBonusDraftLine = (lineKey: string) => {
 setEditCommissionBonusesDraft((prev) => prev.filter((row) => row.key !== lineKey));
 };

 const handleAddSaleCost = async () => {
 if (!sale || !newCostDescription || !newCostAmount || !newCostType) return;
 const costAmt = parseCurrency(newCostAmount);
 if (!Number.isFinite(costAmt) || costAmt <= 0) {
 setToast({ message: t("financialSales.paymentsTab.invalidAmount"), type:"error" });
 return;
 }
 try {
 setIsAddingCost(true);
 await apiService.addSaleCost(sale.id, {
 description: newCostDescription,
 amount: costAmt,
 type: newCostType,
 });
 setToast({ message:"Custo registrado com sucesso", type:"success" });
 setShowAddCost(false);
 setNewCostDescription("");
 setNewCostAmount("");
 setNewCostType("");
 await fetchSale();
 } catch (err: any) {
 setToast({ message: err.message ||"Erro ao registrar custo", type:"error" });
 } finally {
 setIsAddingCost(false);
 }
 };

 const handleSendToPreparationPipeline = async () => {
 if (!sale) return;
 try {
 setSendingPipeline(true);
 await apiService.sendSaleToPreparationPipeline(sale.id);
 setToast({ message: t("financialSales.saleOnboarding.sendSuccess"), type:"success" });
 await fetchSale();
 await fetchDocuments();
 navigate("/operation/preparation");
 } catch (err: unknown) {
 const msg = err instanceof Error ? err.message : t("financialSales.saleOnboarding.sendError");
 setToast({ message: msg, type:"error" });
 } finally {
 setSendingPipeline(false);
 }
 };

 const backFromSaleHref = isPostSalesWizard ? "/post-sales/pipeline" : "/financial/sales";

 if (isLoading) {
 return (
 <div className="space-y-6 max-w-6xl mx-auto">
 <DetailPageSkeleton />
 </div>
 );
 }

 if (!sale) {
 return (
 <div className="space-y-6">
 <ModernButton
 variant="outline"
 onClick={() => navigate(backFromSaleHref)}
 >
 <ArrowLeft className="h-4 w-4 mr-2"/>
 Voltar
 </ModernButton>
 <Card>
 <CardContent className="py-12 text-center">
 <DollarSign className="h-16 w-16 text-muted-foreground mx-auto mb-4"/>
 <h3 className="text-lg font-medium text-foreground mb-2">
 Venda não encontrada
 </h3>
 <p className="text-muted-foreground">
 A venda solicitada não foi encontrada ou você não tem permissão
 para visualizá-la.
 </p>
 </CardContent>
 </Card>
 </div>
 );
 }

 return (
 <div className="space-y-6">
 {/* Header */}
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-4">
 <ModernButton
 variant="outline"
 onClick={() => navigate(backFromSaleHref)}
 >
 <ArrowLeft className="h-4 w-4 mr-2"/>
 Voltar
 </ModernButton>
 <div>
 <h1 className="text-2xl font-bold text-foreground font-display">
 Venda #{sale.id.substring(0, 8)}
 </h1>
 <p className="text-sm text-muted-foreground mt-1">
 Detalhes da venda
 </p>
 </div>
 </div>
 </div>

 {/* Status Card */}
 <Card>
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-4">
 {getStatusIcon(sale.status)}
 <div>
 <div className="flex items-center gap-2">
 <Badge className={getStatusColor(sale.status)}>
 {getStatusLabel(sale.status)}
 </Badge>
 </div>
 <div className="text-sm text-muted-foreground mt-1 space-y-0.5">
 <p>
 {t("financialSales.saleDetailDates.performedOn")}{" "}
 {formatDateToBR(sale.saleDate)}
 </p>
 <p>
 {t("financialSales.saleDetailDates.registeredOn")}{" "}
 {formatDateToBR(sale.createdAt)}
 </p>
 </div>
 </div>
 </div>
 <div className="text-right">
 <div className="text-2xl font-bold text-foreground font-display">
 {formatCurrency(parseFloat(sale.finalValue.toString()))}
 </div>
 <div className="text-sm text-muted-foreground mt-1">
 Valor Total:{""}
 {formatCurrency(parseFloat(sale.totalValue.toString()))}
 </div>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Sale Details Content */}
 <Card>
 <CardContent className="pt-6 space-y-4">
 {isPostSalesWizard && displayOnboardingProgress && sale.status !=="cancelled" && (
 <SaleOnboardingStepper
 progress={displayOnboardingProgress}
 loading={onboardingLoading}
 onGoToTab={() => {}}
 onSendToPipeline={handleSendToPreparationPipeline}
 sending={sendingPipeline}
 canMutate={canMutateOnboarding}
 operationalStage={sale.operationalStage ??"pos_venda"}
 saleCancelled={sale.status as any ==="cancelled"}
 wizardMode
 activeWizardStepIndex={wizardStepIndex}
 />
 )}
 <Tabs
 value={detailsTab}
 onValueChange={(v) => {
 if (isPostSalesWizard) return;
 setDetailsTab(v);
 }}
 className="w-full"
 >
 <TabsList
 className={cn(
 "flex flex-wrap gap-1 h-auto p-1",
 isPostSalesWizard && "hidden"
 )}
 >
 <TabsTrigger value="details">{t("financialSales.saleDetailsTabs.details")}</TabsTrigger>
 <TabsTrigger value="administrative">{t("financialSales.saleDetailsTabs.administrative")}</TabsTrigger>
 <TabsTrigger value="timeline">{t("financialSales.saleDetailsTabs.timeline")}</TabsTrigger>
 <TabsTrigger value="financial">
 {t("financialSales.saleDetailsTabs.financial")}
 </TabsTrigger>
 <TabsTrigger value="commissions">
 {t("financialSales.saleDetailsTabs.commissions")}
 </TabsTrigger>
 <TabsTrigger value="payments">{t("financialSales.saleDetailsTabs.payments")}</TabsTrigger>
 <TabsTrigger value="documents">
 {t("financialSales.saleDetailsTabs.documents")}
 </TabsTrigger>
 </TabsList>

 <TabsContent value="details"className="space-y-4 mt-4">
 <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
 <Card>
 <CardHeader>
 <CardTitle className="text-base">
 Informações Gerais
 </CardTitle>
 </CardHeader>
 <CardContent className="space-y-2">
 <div className="flex items-center gap-2">
 <Calendar className="h-4 w-4 text-muted-foreground"/>
 <span className="text-sm">
 <strong>{t("financialSales.saleDetailDates.saleDateField")}:</strong>{" "}
 {formatDateToBR(sale.saleDate)}
 </span>
 </div>
 <div className="flex items-center gap-2">
 <Calendar className="h-4 w-4 text-muted-foreground"/>
 <span className="text-sm">
 <strong>{t("financialSales.saleDetailDates.registeredAtField")}:</strong>{" "}
 {formatDateToBR(sale.createdAt)}
 </span>
 </div>
 <div className="flex items-center gap-2">
 {getStatusIcon(sale.status)}
 <span className="text-sm">
 <strong>Status:</strong> {getStatusLabel(sale.status)}
 </span>
 </div>
 </CardContent>
 </Card>

 <Card>
 <CardHeader>
 <CardTitle className="text-base">{t("financialSales.proposalCardTitle")}</CardTitle>
 </CardHeader>
 <CardContent>
 <Link
 to={`/proposals/${sale.proposalId}`}
 className="inline-flex items-center gap-2 text-sm font-medium text-accent hover:underline"
 >
 <FileText className="h-4 w-4"/>
 {t("financialSales.viewProposal")}
 </Link>
 <p className="mt-2 text-xs text-muted-foreground font-mono">
 {sale.proposalId}
 </p>
 </CardContent>
 </Card>

 <Card>
 <CardHeader>
 <CardTitle className="text-base">{t("financialSales.customerCard.title")}</CardTitle>
 </CardHeader>
 <CardContent className="space-y-3">
 {leadSnapshotValidationMessages.length > 0 && (
 <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-950 dark:text-amber-100">
 <div className="flex items-start gap-2 font-medium">
 <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
 <span>{t("financialSales.leadSnapshotValidation.title")}</span>
 </div>
 <ul className="mt-2 list-disc space-y-1 pl-5">
 {leadSnapshotValidationMessages.map((m, i) => (
 <li key={i}>{m}</li>
 ))}
 </ul>
 </div>
 )}
 <div className="flex items-center gap-2">
 <User className="h-4 w-4 text-muted-foreground"/>
 <span className="text-sm">
 {sale.customer?.name ||"N/A"}
 </span>
 </div>
 {sale.customer?.document && (
 <p className="text-xs text-muted-foreground mt-1">
 {sale.customer.document}
 </p>
 )}
 {sale.customer?.email && (
 <p className="text-xs text-muted-foreground">
 <span className="font-medium text-foreground/80">
 {t("financialSales.customerCard.email")}:{" "}
 </span>
 {sale.customer.email}
 </p>
 )}
 {sale.customer?.phone && (
 <p className="text-xs text-muted-foreground">
 <span className="font-medium text-foreground/80">
 {t("financialSales.customerCard.phone")}:{" "}
 </span>
 {sale.customer.phone}
 </p>
 )}
 {sale.customer?.address && (
 <p className="text-xs text-muted-foreground">
 <span className="font-medium text-foreground/80">
 {t("financialSales.customerCard.address")}:{" "}
 </span>
 {sale.customer.address}
 </p>
 )}
 {sale.customer?.notes && (
 <p className="text-xs text-muted-foreground whitespace-pre-wrap">
 <span className="font-medium text-foreground/80">
 {t("financialSales.customerCard.notes")}:{" "}
 </span>
 {sale.customer.notes}
 </p>
 )}
 {leadSnapshot && (
 <Accordion>
 <AccordionItem title={t("financialSales.leadSnapshot.title")}>
 {leadSnapshot.capturedAt && (
 <p className="text-xs text-muted-foreground mb-2">
 {t("financialSales.leadSnapshot.capturedAt")}:{" "}
 {formatDateToBR(leadSnapshot.capturedAt)}
 </p>
 )}
 {leadSnapshot.legacyVehicle && (
 <div className="mb-3">
 <p className="text-xs font-medium text-foreground mb-1">
 {t("financialSales.leadSnapshot.legacyVehicleTitle")}
 </p>
 <p className="text-xs text-muted-foreground">
 {[
 leadSnapshot.legacyVehicle.brandName,
 leadSnapshot.legacyVehicle.modelName,
 leadSnapshot.legacyVehicle.plate,
 leadSnapshot.legacyVehicle.modelYear,
 ]
 .filter((x) => x != null && x !== "")
 .join(" · ")}
 </p>
 </div>
 )}
 {leadSnapshot.interests && leadSnapshot.interests.length > 0 && (
 <div className="mb-3">
 <p className="text-xs font-medium text-foreground mb-1">
 {t("financialSales.leadSnapshot.interestsTitle")}
 </p>
 <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-4">
 {leadSnapshot.interests.map((it) => (
 <li key={it.vehicleId}>
 {[
 it.vehicle?.brandName,
 it.vehicle?.modelName,
 it.vehicle?.plate,
 it.vehicle?.modelYear,
 ]
 .filter((x) => x != null && x !== "")
 .join(" · ") || it.vehicleId}
 {it.notes ? ` — ${it.notes}` : ""}
 </li>
 ))}
 </ul>
 </div>
 )}
 {(() => {
 const prefEntries = leadSnapshot.preferences
 ? Object.entries(leadSnapshot.preferences).filter(
 ([, v]) => v != null && v !== ""
 )
 : [];
 if (!prefEntries.length) return null;
 return (
 <div className="mb-3">
 <p className="text-xs font-medium text-foreground mb-1">
 {t("financialSales.leadSnapshot.preferencesTitle")}
 </p>
 <dl className="text-xs text-muted-foreground grid grid-cols-1 gap-1">
 {prefEntries.map(([k, v]) => (
 <div key={k} className="flex gap-2">
 <dt className="shrink-0 font-medium text-foreground/80">{k}</dt>
 <dd className="break-all">
 {typeof v === "object" ? JSON.stringify(v) : String(v)}
 </dd>
 </div>
 ))}
 </dl>
 </div>
 );
 })()}
 {leadSnapshot.proposals && leadSnapshot.proposals.length > 0 && (
 <div className="mb-3 overflow-x-auto">
 <p className="text-xs font-medium text-foreground mb-1">
 {t("financialSales.leadSnapshot.proposalsTitle")}
 </p>
 <Table>
 <TableHeader>
 <TableRow>
 <TableHead className="text-xs">
 {t("financialSales.leadSnapshot.proposalColumns.status")}
 </TableHead>
 <TableHead className="text-xs">
 {t("financialSales.leadSnapshot.proposalColumns.value")}
 </TableHead>
 <TableHead className="text-xs">
 {t("financialSales.leadSnapshot.proposalColumns.final")}
 </TableHead>
 <TableHead className="text-xs">
 {t("financialSales.leadSnapshot.proposalColumns.vehicle")}
 </TableHead>
 <TableHead className="text-xs">
 {t("financialSales.leadSnapshot.proposalColumns.date")}
 </TableHead>
 </TableRow>
 </TableHeader>
 <TableBody>
 {leadSnapshot.proposals.map((p) => (
 <TableRow key={p.id}>
 <TableCell className="text-xs">
 {t(`financialSales.leadSnapshot.proposalStatus.${p.status}`, {
 defaultValue: p.status,
 })}
 </TableCell>
 <TableCell className="text-xs">
 {formatCurrency(p.value ?? 0)}
 </TableCell>
 <TableCell className="text-xs">
 {formatCurrency(p.finalValue ?? 0)}
 </TableCell>
 <TableCell className="text-xs max-w-[140px] truncate">
 {p.vehicleLabel || p.vehicleId || "—"}
 </TableCell>
 <TableCell className="text-xs whitespace-nowrap">
 {p.createdAt ? formatDateToBR(p.createdAt) : "—"}
 </TableCell>
 </TableRow>
 ))}
 </TableBody>
 </Table>
 </div>
 )}
 {(leadSnapshot.emails?.length || leadSnapshot.phones?.length) ? (
 <div>
 <p className="text-xs font-medium text-foreground mb-1">
 {t("financialSales.leadSnapshot.contactsTitle")}
 </p>
 {leadSnapshot.emails?.map((e) => (
 <p key={e.email} className="text-xs text-muted-foreground">
 {e.email}
 {e.isPrimary ? " *" : ""}
 </p>
 ))}
 {leadSnapshot.phones?.map((ph) => (
 <p key={ph.phone} className="text-xs text-muted-foreground">
 {ph.phone}
 {ph.isPrimary ? " *" : ""}
 {ph.isWhatsApp ? " (WhatsApp)" : ""}
 </p>
 ))}
 </div>
 ) : null}
 </AccordionItem>
 </Accordion>
 )}
 {!leadSnapshot && sale.customer?.originLeadId && (
 <p className="text-xs text-muted-foreground">
 {t("financialSales.leadSnapshot.emptySnapshot")}
 </p>
 )}
 </CardContent>
 </Card>

 <Card>
 <CardHeader>
 <CardTitle className="text-base">{t("financialSales.vehicleCard.title")}</CardTitle>
 </CardHeader>
 <CardContent>
 <div className="flex items-center gap-2">
 <Car className="h-4 w-4 text-muted-foreground"/>
 <span className="text-sm">
 {(sale.vehicle as any)?.brand?.name ||""}{""}
 {(sale.vehicle as any)?.model?.name ||""}{""}
 {(sale.vehicle as any)?.modelYear ||""}
 </span>
 </div>
 {(sale.vehicle as any)?.plate && (
 <p className="text-xs text-muted-foreground mt-1">
 {t("financialSales.vehicleCard.plate")}: {(sale.vehicle as any).plate}
 </p>
 )}
 </CardContent>
 </Card>

 <Card>
 <CardHeader>
 <CardTitle className="text-base">{t("financialSales.salespersonCard.title")}</CardTitle>
 </CardHeader>
 <CardContent>
 <div className="flex items-center gap-2">
 <User className="h-4 w-4 text-muted-foreground"/>
 <span className="text-sm">
 {sale.salesperson?.name ||"N/A"}
 </span>
 </div>
 {sale.salesperson?.email && (
 <p className="text-xs text-muted-foreground mt-1">
 {sale.salesperson.email}
 </p>
 )}
 </CardContent>
 </Card>
 </div>

 {sale.items && sale.items.length > 0 && (
 <Card className="mt-4">
 <CardHeader>
 <CardTitle className="text-base">{t("financialSales.saleItemsTitle")}</CardTitle>
 </CardHeader>
 <CardContent>
<div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
<div className="rounded-lg border border-border p-3">
<p className="text-xs text-muted-foreground">
{t("financialSales.commissions.summaryVehicleValue")}
</p>
<p className="text-lg font-semibold text-foreground">
{formatCurrency(commissionTotals.vehicleValue)}
</p>
</div>
<div className="rounded-lg border border-border p-3">
<p className="text-xs text-muted-foreground">
{t("financialSales.commissions.summaryCommissionsTotal")}
</p>
<p className="text-lg font-semibold text-foreground">
{formatCurrency(commissionTotals.commissionsTotal)}
</p>
</div>
<div className="rounded-lg border border-border p-3">
<p className="text-xs text-muted-foreground">
{t("financialSales.commissions.summaryVehicleMinusCommissions")}
</p>
<p className="text-lg font-semibold text-foreground">
{formatCurrency(commissionTotals.vehicleMinusCommissions)}
</p>
</div>
</div>
 <Table>
 <TableHeader>
 <TableRow>
 <TableHead>{t("financialSales.saleItemDescription")}</TableHead>
 <TableHead className="text-right">{t("financialSales.saleItemQty")}</TableHead>
 <TableHead className="text-right">{t("financialSales.saleItemUnit")}</TableHead>
 <TableHead className="text-right">{t("financialSales.saleItemTotal")}</TableHead>
 </TableRow>
 </TableHeader>
 <TableBody>
 {sale.items.map((item) => (
 <TableRow key={item.id}>
 <TableCell>{item.description}</TableCell>
 <TableCell className="text-right">{item.quantity}</TableCell>
 <TableCell className="text-right">
 {formatCurrency(parseFloat(item.unitPrice.toString()))}
 </TableCell>
 <TableCell className="text-right font-medium">
 {formatCurrency(parseFloat(item.totalPrice.toString()))}
 </TableCell>
 </TableRow>
 ))}
 </TableBody>
 </Table>
 </CardContent>
 </Card>
 )}
 </TabsContent>

 <TabsContent value="administrative" className="space-y-4 mt-4">
 <Card>
 <CardHeader>
 <CardTitle className="text-base">{t("financialSales.saleLifecycle.lifecycleChecklistsTitle")}</CardTitle>
 </CardHeader>
 <CardContent className="space-y-6">
 {sale.status ==="cancelled" ? (
 <p className="text-sm text-muted-foreground">{t("financialSales.saleLifecycle.lifecycleCancelledHint")}</p>
 ) : !displayOnboardingProgress ? (
 <p className="text-sm text-muted-foreground">{t("actions.loading")}</p>
 ) : (
 <>
 {SALE_LIFECYCLE_PHASE_ORDER.slice(0, 5).map((phase) => {
 const complete = isLifecycleOnboardingPhaseComplete(phase, displayOnboardingProgress);
 const tab = getLifecyclePhaseTab(phase);
 return (
 <div key={phase} className="space-y-3 border-b border-border pb-5 last:border-b-0">
 <div className="flex flex-wrap items-center justify-between gap-2">
 <h4 className="text-sm font-semibold text-foreground">
 {t(`financialSales.saleLifecycle.phases.${phase}`)}
 </h4>
 <span className="text-xs text-muted-foreground">
 {complete
 ? t("financialSales.saleLifecycle.lifecycleDone")
 : t("financialSales.saleLifecycle.lifecyclePending")}
 </span>
 </div>
 {phase ==="documentation" && !isPostSalesWizard && (
 <SaleOnboardingRequirementsCard
 saleId={sale.id}
 customerId={sale.customerId}
 progress={displayOnboardingProgress}
 saleDocuments={saleDocuments}
 customerDocuments={customerDocuments}
 canEdit={canMutateOnboarding}
 onUpdated={async () => {
 await refreshOnboarding(sale.id);
 await fetchDocuments();
 }}
 onToast={(message, type) => setToast({ message, type })}
 />
 )}
 {tab && (
 <ModernButton type="button" variant="outline" size="sm" onClick={() => setDetailsTab(tab)}>
 {t("financialSales.saleLifecycle.lifecycleOpenTab")}
 </ModernButton>
 )}
 </div>
 );
 })}
 </>
 )}
 <div className="space-y-3">
 <h4 className="text-sm font-semibold text-foreground">
 {t("financialSales.saleLifecycle.phases.ready_for_delivery")}
 </h4>
 {(() => {
 const items = (sale.checklistItems ?? [])
 .filter((i) => (i.operationalStage as any) ==="entrega")
 .sort((a, b) => a.sortOrder - b.sortOrder);
 if (items.length === 0) {
 return (
<div className="space-y-2">
<p className="text-sm text-muted-foreground">{t("operation.saleDetail.checklistEmpty")}</p>
<Link to="/settings/operation-pipeline" className="inline-flex">
<ModernButton type="button" variant="outline" size="sm">
{t("operation.saleDetail.configureCheckpoints")}
</ModernButton>
</Link>
</div>
 );
 }
 return (
 <div className="space-y-3">
 {items.map((item) => (
 <label key={item.id} className="flex items-center gap-3 cursor-pointer">
 <input
 type="checkbox"
 className="rounded border-border"
 checked={!!item.completedAt}
 disabled={savingChecklistId === item.id}
 onChange={(e) => void handleChecklistToggle(item, e.target.checked)}
 />
 <span className="text-sm">
 {item.title}
 {item.required && <span className="text-destructive"> *</span>}
 </span>
 </label>
 ))}
 </div>
 );
 })()}
 </div>
 </CardContent>
 </Card>
 </TabsContent>

 <TabsContent value="timeline" className="space-y-4 mt-4">
 <Card>
 <CardHeader>
 <CardTitle className="text-base flex items-center gap-2">
 <History className="h-5 w-5" />
 {t("financialSales.saleLifecycle.timelineTitle")}
 </CardTitle>
 </CardHeader>
 <CardContent>
 {timelineLoading ? (
 <p className="text-sm text-muted-foreground">{t("actions.loading")}</p>
 ) : timelineEvents.length === 0 ? (
 <p className="text-sm text-muted-foreground">{t("financialSales.saleLifecycle.timelineEmpty")}</p>
 ) : (
 <ul className="space-y-3 border-l border-border pl-4 ml-1">
 {timelineEvents.map((ev, idx) => (
 <li key={`${ev.occurredAt}-${idx}`} className="relative">
 <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-accent" />
 <p className="text-xs text-muted-foreground">
 {formatDateToBR(ev.occurredAt)}
 </p>
 <p className="text-sm font-medium text-foreground">
 {t(`financialSales.saleLifecycle.timelineEventTitles.${ev.type}` as const, {
 defaultValue: ev.title,
 })}
 </p>
 </li>
 ))}
 </ul>
 )}
 </CardContent>
 </Card>
 </TabsContent>

 <TabsContent value="financial"className="space-y-4 mt-4">
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <Card>
 <CardHeader>
 <CardTitle className="text-base flex items-center gap-2">
 <DollarSign className="h-5 w-5"/>
 Valores da Venda
 </CardTitle>
 </CardHeader>
 <CardContent className="space-y-3">
 <div className="flex justify-between items-center">
 <span className="text-sm text-muted-foreground">
 Valor Total:
 </span>
 <span className="font-semibold text-lg">
 {formatCurrency(parseFloat(sale.totalValue.toString()))}
 </span>
 </div>
 <div className="flex justify-between items-center">
 <span className="text-sm text-muted-foreground">
 Valor Final:
 </span>
 <span className="font-semibold text-lg text-green-600">
 {formatCurrency(parseFloat(sale.finalValue.toString()))}
 </span>
 </div>
 {paymentTotals && (
 <>
 <div className="flex justify-between items-center border-t border-border pt-3">
 <span className="text-sm text-muted-foreground">
 {t("financialSales.paymentsTab.totalConfirmed")}
 </span>
 <span className="font-semibold text-lg text-green-600">
 {formatCurrency(paymentTotals.totalConfirmed)}
 </span>
 </div>
 <div className="flex justify-between items-center">
 <span className="text-sm text-muted-foreground">
 {t("financialSales.paymentsTab.remaining")}
 </span>
 <span className="font-semibold text-lg">
 {formatCurrency(paymentTotals.remainingToPay)}
 </span>
 </div>
 </>
 )}
 </CardContent>
 </Card>

 <Card>
 <CardHeader>
 <CardTitle className="text-base flex items-center gap-2">
 <Receipt className="h-5 w-5"/>
 Resumo Financeiro
 </CardTitle>
 </CardHeader>
 <CardContent className="space-y-3">
 {sale.invoices && sale.invoices.length > 0 && (
 <div>
 <p className="text-xs text-muted-foreground mb-2">
 Faturas:
 </p>
 <p className="text-lg font-semibold">
 {sale.invoices.length}{""}
 {sale.invoices.length === 1 ?"fatura":"faturas"}
 </p>
 <p className="text-sm text-muted-foreground">
 Total:{""}
 {formatCurrency(
 sale.invoices.reduce(
 (sum, inv) =>
 sum + parseFloat(inv.totalAmount.toString()),
 0
 )
 )}
 </p>
 </div>
 )}
 {sale.commissions && sale.commissions.length > 0 && (
 <div className="border-t pt-3 mt-3">
 <p className="text-xs text-muted-foreground mb-2">
 Comissões:
 </p>
 <p className="text-lg font-semibold">
 {sale.commissions.length}{""}
 {sale.commissions.length === 1
 ?"comissão"
 :"comissões"}
 </p>
 <p className="text-sm text-muted-foreground">
 Total:{""}
 {formatCurrency(
 sale.commissions.reduce(
 (sum, comm) =>
 sum +
 parseFloat(comm.calculatedValue.toString()) +
 sumCommissionBonuses(comm),
 0
 )
 )}
 </p>
 </div>
 )}
 </CardContent>
 </Card>
 </div>

 <Card className="mt-4">
 <CardHeader>
 <CardTitle className="text-base flex items-center gap-2">
 <Download className="h-5 w-5"/>
 {t("financialSales.fiscalExport.title")}
 </CardTitle>
 </CardHeader>
 <CardContent className="space-y-3">
 <p className="text-sm text-muted-foreground">
 {t("financialSales.fiscalExport.description")}
 </p>
 {fiscalReadiness?.ready ? (
 <p className="text-sm text-green-600 dark:text-green-400">
 {t("financialSales.fiscalExport.readyHint")}
 </p>
 ) : (
 <>
 <p className="text-sm font-medium">{t("financialSales.fiscalExport.missingTitle")}</p>
 <ul className="list-disc pl-5 text-sm space-y-1 text-muted-foreground">
 {(fiscalReadiness?.missingFields ?? []).map((m) => (
 <li key={m.key}>{t(`financialSales.${m.labelKey}`)}</li>
 ))}
 </ul>
 </>
 )}
 <div className="flex flex-wrap gap-2 pt-2">
 <Link
 to="/settings/dealership"
 className="inline-flex items-center justify-center rounded-md border border-border bg-background px-3 py-2 text-sm font-medium hover:bg-muted"
 >
 {t("financialSales.fiscalExport.editDealership")}
 </Link>
 <Link
 to={`/vehicles/edit/${sale.vehicleId}`}
 className="inline-flex items-center justify-center rounded-md border border-border bg-background px-3 py-2 text-sm font-medium hover:bg-muted"
 >
 {t("financialSales.fiscalExport.editVehicle")}
 </Link>
 <Link
 to={`/customers/edit/${sale.customerId}`}
 className="inline-flex items-center justify-center rounded-md border border-border bg-background px-3 py-2 text-sm font-medium hover:bg-muted"
 >
 {t("financialSales.fiscalExport.editCustomer")}
 </Link>
 <ModernButton
 type="button"
 variant="primary"
 className="inline-flex items-center gap-2"
 disabled={fiscalPdfLoading}
 onClick={() => void handleDownloadFiscalPdf()}
 >
 {fiscalPdfLoading ? (
 <Loader2 className="h-4 w-4 animate-spin"/>
 ) : (
 <FileDown className="h-4 w-4"/>
 )}
 {t("financialSales.fiscalExport.downloadPdf")}
 </ModernButton>
 <ModernButton
 type="button"
 variant="secondary"
 className="inline-flex items-center gap-2"
 onClick={() => void handleDownloadFiscalJson()}
 >
 <Download className="h-4 w-4"/>
 {t("financialSales.fiscalExport.downloadJson")}
 </ModernButton>
 </div>
 </CardContent>
 </Card>

 {sale.invoices && sale.invoices.length > 0 && (
 <Card>
 <CardHeader>
 <CardTitle className="text-base flex items-center gap-2">
 <FileText className="h-5 w-5"/>
 Faturas ({sale.invoices.length})
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="space-y-3">
 {sale.invoices.map((invoice: Invoice) => {
 const invoiceTotal = parseFloat(
 invoice.totalAmount.toString()
 );
 const invoicePaid = parseFloat(
 invoice.paidAmount.toString()
 );
 const invoiceRemaining = invoiceTotal - invoicePaid;
 const getInvoiceStatusColor = (
 status: Invoice["status"]
 ) => {
 switch (status) {
 case"paid":
 return"bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
 case"issued":
 return"bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
 case"overdue":
 return"bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
 case"cancelled":
 return"bg-surface text-foreground dark:text-muted-foreground";
 default:
 return"bg-surface text-foreground dark:text-muted-foreground";
 }
 };
 const getInvoiceStatusLabel = (
 status: Invoice["status"]
 ) => {
 const labels: Record<Invoice["status"], string> = {
 draft:"Rascunho",
 issued:"Emitida",
 paid:"Paga",
 overdue:"Vencida",
 cancelled:"Cancelada",
 };
 return labels[status] || status;
 };
 return (
 <div
 key={invoice.id}
 className="p-4 border border-border rounded-lg hover:bg-surface transition-colors"
 >
 <div className="flex items-start justify-between mb-2">
 <div className="flex-1">
 <div className="flex items-center gap-2 mb-1">
 <span className="font-semibold text-foreground">
 Fatura #{invoice.invoiceNumber}
 </span>
 <Badge
 className={getInvoiceStatusColor(
 invoice.status
 )}
 >
 {getInvoiceStatusLabel(invoice.status)}
 </Badge>
 </div>
 <div className="text-sm text-muted-foreground space-y-1">
 <p>
 Emitida em:{""}
 {formatDateToBR(invoice.issueDate)}
 </p>
 <p>
 Vencimento:{""}
 {formatDateToBR(invoice.dueDate)}
 </p>
 </div>
 </div>
 <ModernButton
 variant="outline"
 size="sm"
 onClick={() =>
 navigate(
 `/financial/invoices/${invoice.id}`
 )
 }
 >
 <Eye className="h-4 w-4 mr-1"/>
 Ver
 </ModernButton>
 </div>
 <div className="grid grid-cols-3 gap-4 pt-2 border-t border-border">
 <div>
 <p className="text-xs text-muted-foreground">
 Total
 </p>
 <p className="font-semibold">
 {formatCurrency(invoiceTotal)}
 </p>
 </div>
 <div>
 <p className="text-xs text-muted-foreground">
 Pago
 </p>
 <p className="font-semibold text-green-600">
 {formatCurrency(invoicePaid)}
 </p>
 </div>
 <div>
 <p className="text-xs text-muted-foreground">
 Restante
 </p>
 <p className="font-semibold text-amber-600">
 {formatCurrency(invoiceRemaining)}
 </p>
 </div>
 </div>
 </div>
 );
 })}
 </div>
 </CardContent>
 </Card>
 )}

 <Card>
 <CardHeader>
 <CardTitle className="text-base flex items-center justify-between">
 <div className="flex items-center gap-2">
 <Wrench className="h-5 w-5"/>
 {t("financialSales.preparationCostsTitle")}
 </div>
 <ModernButton
 variant="outline"
 size="sm"
 onClick={() => setShowAddCost(!showAddCost)}
 >
 <Plus className="h-4 w-4 mr-1"/>
 {t("financialSales.preparationCostsAdd")}
 </ModernButton>
 </CardTitle>
 </CardHeader>
 <CardContent>
 {showAddCost && (
 <div className="mb-4 p-4 border border-border rounded-md space-y-3">
 <div>
 <Label>{t("financialSales.preparationCostsDescription")} *</Label>
 <input
 type="text"
 className="w-full mt-1 px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm"
 placeholder={t("financialSales.preparationCostsDescriptionPlaceholder")}
 value={newCostDescription}
 onChange={(e) => setNewCostDescription(e.target.value)}
 />
 </div>
 <div className="grid grid-cols-2 gap-3">
 <div>
 <Label>{t("financialSales.preparationCostsAmount")} *</Label>
 <PriceInput
 className="mt-1"
 value={newCostAmount}
 onChange={setNewCostAmount}
 placeholder="R$ 0,00"
 />
 </div>
 <div>
 <Label>{t("financialSales.preparationCostsType")} *</Label>
 <Select value={newCostType} onValueChange={setNewCostType}>
 <SelectTrigger className="mt-1">
 <SelectValue placeholder={t("financialSales.preparationCostsTypePlaceholder")}/>
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="mechanical">{t("financialSales.costTypes.mechanical")}</SelectItem>
 <SelectItem value="bodywork">{t("financialSales.costTypes.bodywork")}</SelectItem>
 <SelectItem value="documentation">{t("financialSales.costTypes.documentation")}</SelectItem>
 <SelectItem value="cleaning">{t("financialSales.costTypes.cleaning")}</SelectItem>
 <SelectItem value="accessories">{t("financialSales.costTypes.accessories")}</SelectItem>
 <SelectItem value="other">{t("financialSales.costTypes.other")}</SelectItem>
 </SelectContent>
 </Select>
 </div>
 </div>
 <div className="flex justify-end gap-2">
 <ModernButton variant="outline" size="sm" onClick={() => setShowAddCost(false)} disabled={isAddingCost}>
 {t("actions.cancel")}
 </ModernButton>
 <ModernButton size="sm" onClick={handleAddSaleCost} disabled={!newCostDescription || !newCostAmount || !newCostType || isAddingCost}>
 {isAddingCost ? t("actions.loading") : t("actions.save")}
 </ModernButton>
 </div>
 </div>
 )}
 {sale.costs && sale.costs.length > 0 ? (
 <Table>
 <TableHeader>
 <TableRow>
 <TableHead>{t("financialSales.preparationCostsDescription")}</TableHead>
 <TableHead>{t("financialSales.preparationCostsType")}</TableHead>
 <TableHead className="text-right">{t("financialSales.preparationCostsAmount")}</TableHead>
 <TableHead>{t("common.date")}</TableHead>
 </TableRow>
 </TableHeader>
 <TableBody>
 {sale.costs.map((c: SaleCost) => {
 const typeLabels: Record<string, string> = {
 mechanical: t("financialSales.costTypes.mechanical"),
 bodywork: t("financialSales.costTypes.bodywork"),
 documentation: t("financialSales.costTypes.documentation"),
 cleaning: t("financialSales.costTypes.cleaning"),
 accessories: t("financialSales.costTypes.accessories"),
 other: t("financialSales.costTypes.other"),
 };
 return (
 <TableRow key={c.id}>
 <TableCell>{c.description}</TableCell>
 <TableCell>{typeLabels[c.type] || c.type}</TableCell>
 <TableCell className="text-right font-medium">
 {formatCurrency(parseFloat(c.amount.toString()))}
 </TableCell>
 <TableCell>{formatDateToBR(c.createdAt)}</TableCell>
 </TableRow>
 );
 })}
 </TableBody>
 </Table>
 ) : (
 !showAddCost && (
 <p className="text-sm text-muted-foreground text-center py-4">
 {t("financialSales.preparationCostsEmpty")}
 </p>
 )
 )}
 </CardContent>
 </Card>
 </TabsContent>

<TabsContent value="commissions"className="space-y-4 mt-4">
  <CommissionActiveRulesTable
  rules={sortedActiveCommissionRules}
  sale={sale}
  evalContext={saleCommissionEvalContext as any}
  />
{sale.commissions && sale.commissions.length > 0 ? (
  <Card>
 <CardHeader>
 <div className="flex items-center justify-between">
 <CardTitle className="text-base flex items-center gap-2">
 <Percent className="h-5 w-5"/>
Comissões ({visibleCommissions.length})
 </CardTitle>
 <ModernButton
 onClick={() => setAddCommissionDialogOpen(true)}
 size="sm"
 disabled={!canMutateCommissions || isCommissionsBackendBusy}
 >
 <Plus className="h-4 w-4 mr-2"/>
 Adicionar Comissão
 </ModernButton>
 </div>
 </CardHeader>
 <CardContent>
 <Table>
 <TableHeader>
 <TableRow>
 <TableHead>Vendedor</TableHead>
 <TableHead>Regra</TableHead>
 <TableHead className="text-right">Base</TableHead>
 <TableHead className="text-right">%</TableHead>
 <TableHead className="text-right">{t("financialSales.commissions.fixedValueColumn")}</TableHead>
 <TableHead className="text-right">{t("financialSales.commissions.originalColumn")}</TableHead>
 <TableHead className="text-right">{t("financialSales.commissions.agreedColumn")}</TableHead>
 <TableHead className="text-right">{t("financialSales.commissions.bonusColumn")}</TableHead>
<TableHead>{t("financialSales.commissions.bonusValuesColumn")}</TableHead>
 <TableHead className="text-right">{t("financialSales.commissions.totalWithBonusesColumn")}</TableHead>
 <TableHead className="text-right">{t("financialSales.commissions.differenceColumn")}</TableHead>
 <TableHead>Status</TableHead>
 <TableHead>Pago em</TableHead>
{canMarkAsPaid && !isSentToPreparation && <TableHead>Ações</TableHead>}
 </TableRow>
 </TableHeader>
 <TableBody>
{visibleCommissions.map((commission: Commission) => {
 const fallbackRule = resolveCommissionFallbackRule(commission);
 const ruleMeta = commission.rule ?? fallbackRule;
 const display = resolveCommissionDisplay(commission, sale, fallbackRule);
 const opd = commissionOriginalPaidAndDiff(commission, sale, fallbackRule);
const draft = commissionDrafts[commission.id];
const agreedToDisplay = draft?.calculatedValue ?? opd.paid;
const baseToDisplay = draft?.baseValue ?? display.baseValue;
const percentageToDisplayRaw = draft?.percentage ?? display.percentage;
const percentageToDisplay =
percentageToDisplayRaw > 0
? percentageToDisplayRaw
: ruleMeta?.commissionType === "fixed" && baseToDisplay > 0
? (agreedToDisplay / baseToDisplay) * 100
: percentageToDisplayRaw;
const diffLive = agreedToDisplay - opd.original;
const bonusSum = sumCommissionBonuses(commission);
const ruleBonusSum =
(ruleMeta?.ruleBonuses ?? []).reduce(
(sum, b) => sum + (typeof b.amount === "string" ? parseFloat(b.amount) || 0 : Number(b.amount) || 0),
0
);
const bonusValuesDisplay =
bonusSum > 0
? formatCurrency(bonusSum)
: ruleBonusSum > 0
? formatCurrency(ruleBonusSum)
: "—";
const totalWithBonuses = agreedToDisplay + bonusSum;
 const getCommissionStatusColor = (
 status: Commission["status"]
 ) => {
 switch (status) {
 case"paid":
 return"bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
 case"pending":
 return"bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
 case"cancelled":
 return"bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
 default:
 return"bg-surface text-foreground dark:text-muted-foreground";
 }
 };
 const getCommissionStatusLabel = (
 status: Commission["status"]
 ) => {
 const labels: Record<Commission["status"], string> =
 {
 pending:"Pendente",
 paid:"Pago",
 cancelled:"Cancelada",
 };
 return labels[status] || status;
 };
 return (
 <React.Fragment key={commission.id}>
 <TableRow>
 <TableCell>
 {commission.salesperson?.name ||"N/A"}
 </TableCell>
 <TableCell>
 <div className="flex flex-wrap items-center gap-2">
 <span>{ruleMeta?.name ||"N/A"}</span>
 {ruleMeta?.isMandatory && (
 <Badge variant="warning" className="text-xs">
 {t("financialSales.commissions.mandatoryBadge")}
 </Badge>
 )}
 </div>
 </TableCell>
 <TableCell className="text-right">
{formatCurrency(baseToDisplay)}
 </TableCell>
 <TableCell className="text-right">
{percentageToDisplay.toFixed(2)}%
 </TableCell>
 <TableCell className="text-right text-muted-foreground">
 {ruleMeta?.commissionType === "mixed" ? (
 <div className="text-right leading-tight">
 <div>
 {t("financialSales.commissions.mixedBreakdown", {
 fixed: formatCurrency(draft?.fixedValue ?? commissionRuleFixedAmount(ruleMeta)),
 percent: `${percentageToDisplay.toFixed(2)}%`,
 totalPercent:
 baseToDisplay > 0
 ? `${((agreedToDisplay / baseToDisplay) * 100).toFixed(2)}%`
 : "—",
 })}
 </div>
 </div>
 ) : commissionRuleHasFixedPortion(ruleMeta) ? (
 formatCurrency(draft?.fixedValue ?? commissionRuleFixedAmount(ruleMeta))
 ) : (
 "—"
 )}
 </TableCell>
 <TableCell className="text-right text-muted-foreground">
 {formatCurrency(opd.original)}
 </TableCell>
 <TableCell className="text-right font-semibold">
{formatCurrency(agreedToDisplay)}
 </TableCell>
 <TableCell className="text-right text-muted-foreground">
 {bonusSum > 0 ? formatCurrency(bonusSum) : "—"}
 </TableCell>
<TableCell className="text-sm text-muted-foreground">
{bonusValuesDisplay}
</TableCell>
 <TableCell className="text-right font-semibold">
 {formatCurrency(totalWithBonuses)}
 </TableCell>
 <TableCell
 className={cn(
 "text-right font-medium",
 diffLive < 0 && "text-amber-700 dark:text-amber-400",
 diffLive > 0 && "text-emerald-700 dark:text-emerald-400"
 )}
 >
 {diffLive === 0 ? "—" : formatCurrency(diffLive)}
 </TableCell>
 <TableCell>
 <Badge
 className={getCommissionStatusColor(
 commission.status
 )}
 >
 {getCommissionStatusLabel(commission.status)}
 </Badge>
 </TableCell>
 <TableCell>
 {commission.paidAt
 ? formatDateToBR(commission.paidAt)
 :"-"}
 </TableCell>
{canMarkAsPaid && !isSentToPreparation && (
 <TableCell>
 <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
{commission.status ==="pending"&& canMutateCommissions && (
 <>
 <ModernButton
 variant="outline"
 size="sm"
 onClick={() => beginEditCommission(commission)}
 disabled={isCommissionsBackendBusy}
 >
 <Pencil className="h-4 w-4 mr-1"/>
 {t("financialSales.commissions.edit")}
 </ModernButton>
 <ModernButton
 variant="outline"
 size="sm"
 onClick={() => setCommissionIdPendingDelete(commission.id)}
 className="text-destructive border-destructive/40 hover:bg-destructive/10"
 disabled={isCommissionsBackendBusy}
 >
 <Trash2 className="h-4 w-4 mr-1"/>
 {t("financialSales.commissions.remove")}
 </ModernButton>
 </>
 )}
{commission.status ==="pending"&& canMarkCommissionAsPaid && (
 <ModernButton
 variant="primary"
 size="sm"
 onClick={() => void handleMarkAsPaid(commission.id)}
 className="bg-green-600 hover:bg-green-700 text-white"
 disabled={isCommissionsBackendBusy}
 >
 {markingCommissionPaidId === commission.id ? (
 <Loader2 className="h-4 w-4 mr-1 animate-spin" aria-hidden />
 ) : (
 <CheckCircle className="h-4 w-4 mr-1" aria-hidden />
 )}
 {markingCommissionPaidId === commission.id ? t("actions.loading") : "Marcar como Paga"}
 </ModernButton>
 )}
 </div>
 </TableCell>
 )}
 </TableRow>
 </React.Fragment>
 );
 })}
 </TableBody>
 </Table>
 {editingCommissionId &&
visibleCommissions.some((c) => c.id === editingCommissionId && c.status === "pending") && (
 <div className="mt-6 space-y-4 border-t border-border pt-6">
 <p className="text-sm font-medium text-foreground">
 {t("financialSales.commissions.edit")}
 </p>
 <div
 className={cn(
 "grid grid-cols-1 gap-4",
 editingRuleForPanel && commissionRuleHasFixedPortion(editingRuleForPanel)
 ? "sm:grid-cols-2 lg:grid-cols-4"
 : "sm:grid-cols-3"
 )}
 >
 <div>
 <Label htmlFor="edit-commission-base">{t("financialSales.commissions.base")}</Label>
 <PriceInput
 id="edit-commission-base"
 value={editCommissionBase}
 onChange={setEditCommissionBase}
 className="mt-1"
 placeholder="R$ 0,00"
 />
 </div>
 <div>
 <Label htmlFor="edit-commission-pct">{t("financialSales.commissions.percent")}</Label>
 <Input
 id="edit-commission-pct"
 type="text"
 inputMode="decimal"
 value={editCommissionPercent}
 onChange={(e) => setEditCommissionPercent(e.target.value)}
 className="mt-1"
 />
 </div>
 {editingRuleForPanel && commissionRuleHasFixedPortion(editingRuleForPanel) && (
 <div>
 <Label htmlFor="edit-commission-fixed">{t("financialSales.commissions.fixedValueLabel")}</Label>
 <PriceInput
 id="edit-commission-fixed"
 value={editCommissionFixed}
 onChange={setEditCommissionFixed}
 className="mt-1"
 placeholder="R$ 0,00"
 />
 </div>
 )}
 <div>
 <Label htmlFor="edit-commission-val">{t("financialSales.commissions.calculatedValue")}</Label>
 {editingRuleForPanel &&
 (editingRuleForPanel.commissionType === "mixed" ||
 editingRuleForPanel.commissionType === "fixed") ? (
<>
<div
id="edit-commission-val"
className="mt-1 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm font-medium tabular-nums"
>
{computedEditCommissionTotal !== null
? formatCurrency(computedEditCommissionTotal)
: "—"}
</div>
{editingRuleForPanel?.commissionType === "mixed" && (
<p className="mt-1 text-xs text-muted-foreground">
{t("financialSales.commissions.mixedBreakdown", {
fixed: formatCurrency(parseCurrency(editCommissionFixed)),
percent: `${editCommissionPercent || "0"}%`,
totalPercent:
computedEditCommissionTotal !== null && parseCurrency(editCommissionBase) > 0
? `${((computedEditCommissionTotal / parseCurrency(editCommissionBase)) * 100).toFixed(2)}%`
: "—",
})}
</p>
)}
</>
 ) : (
 <PriceInput
 id="edit-commission-val"
 value={editCommissionCalculated}
 onChange={setEditCommissionCalculated}
 className="mt-1"
 placeholder="R$ 0,00"
 />
 )}
 </div>
 </div>
 {canMutateCommissions &&
 editingCommissionForPanel?.status === "pending" && (
 <div className="space-y-3 rounded-lg border border-border bg-muted/10 p-4">
 <p className="text-sm font-medium text-foreground">
 {t("financialSales.commissions.bonusSectionTitle")}
 </p>
 {editCommissionBonusesDraft.length > 0 && (
 <ul className="space-y-2">
 {editCommissionBonusesDraft.map((b) => (
 <li
 key={b.key}
 className="flex flex-wrap items-center justify-between gap-2 text-sm"
 >
 <span className="text-foreground">{b.label}</span>
 <span className="flex items-center gap-2">
 <span className="font-medium tabular-nums">
 {formatCurrency(parseCurrency(b.amount))}
 </span>
 <ModernButton
 type="button"
 variant="outline"
 size="sm"
 className="h-8 px-2 text-destructive"
 disabled={isCommissionsBackendBusy}
 onClick={() => handleRemoveCommissionBonusDraftLine(b.key)}
 >
 <Trash2 className="h-4 w-4" aria-hidden />
 </ModernButton>
 </span>
 </li>
 ))}
 </ul>
 )}
 <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-end">
 <div className="flex-1 min-w-[160px]">
 <Label className="text-xs text-muted-foreground">
 {t("financialSales.commissions.bonusLabelPlaceholder")}
 </Label>
 <Input
 value={editPanelBonusDraft.label}
 onChange={(e) =>
 setEditPanelBonusDraft((d) => ({ ...d, label: e.target.value }))
 }
 className="mt-1"
 />
 </div>
 <div className="w-full sm:w-40">
 <Label className="text-xs text-muted-foreground">
 {t("financialSales.commissions.bonusAmountLabel")}
 </Label>
 <PriceInput
 value={editPanelBonusDraft.amount}
 onChange={(v) => setEditPanelBonusDraft((d) => ({ ...d, amount: v }))}
 className="mt-1"
 />
 </div>
 <ModernButton
 type="button"
 size="sm"
 className="sm:mb-0.5"
 disabled={
 isCommissionsBackendBusy ||
 !editPanelBonusDraft.label.trim()
 }
 onClick={handleAddCommissionBonusDraftLine}
 >
 {t("financialSales.commissions.bonusAdd")}
 </ModernButton>
 </div>
 </div>
 )}
 <div className="flex flex-wrap gap-2">
 <ModernButton
 onClick={() => void handleSaveCommissionEdit()}
 disabled={isCommissionsBackendBusy}
 >
 {isSavingCommissionEdit ? (
 <Loader2 className="h-4 w-4 mr-1.5 animate-spin" aria-hidden />
 ) : null}
 {isSavingCommissionEdit ? t("actions.loading") : t("actions.save")}
 </ModernButton>
 <ModernButton variant="outline" onClick={cancelEditCommission} disabled={isCommissionsBackendBusy}>
 {t("actions.cancel")}
 </ModernButton>
 </div>
 </div>
 )}
 <div className="mt-6 flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
 <p className="text-sm text-muted-foreground">
 {sale.commissionsConfirmedAt
 ? t("financialSales.commissions.stepConfirmedHint", {
 date: formatDateToBR(sale.commissionsConfirmedAt),
 })
 : t("financialSales.commissions.stepConfirmHint")}
 </p>
{canMarkAsPaid && !isSentToPreparation && (
<>
{sale.commissionsConfirmedAt && !isCommissionsEditingUnlocked && (
<ModernButton
variant="outline"
onClick={() => setIsCommissionsEditingUnlocked(true)}
disabled={isCommissionsBackendBusy}
>
{t("financialSales.commissions.editAfterConfirm")}
</ModernButton>
)}
 <ModernButton
 disabled={Boolean(sale.commissionsConfirmedAt) || isCommissionsBackendBusy}
 onClick={() => void handleConfirmCommissionsStep()}
 >
 {isConfirmingCommissionsStep ? (
 <Loader2 className="h-4 w-4 mr-1.5 animate-spin" aria-hidden />
 ) : null}
 {isConfirmingCommissionsStep
 ? t("actions.loading")
 : t("financialSales.commissions.confirmStepCta")}
 </ModernButton>
</>
 )}
 </div>
{isSentToPreparation && (
<p className="mt-2 text-xs text-muted-foreground">
{t("financialSales.commissions.lockedAfterPreparation")}
</p>
)}
 </CardContent>
 </Card>
 ) : (
 <Card>
 <CardContent className="py-12 text-center">
 <Percent className="h-12 w-12 text-muted-foreground mx-auto mb-4"/>
 <p className="text-muted-foreground mb-4">
 Nenhuma comissão registrada para esta venda
 </p>
 <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
 <ModernButton
 onClick={() => setAddCommissionDialogOpen(true)}
 variant="outline"
 disabled={!canMutateCommissions || isCommissionsBackendBusy}
 >
 <Plus className="h-4 w-4 mr-2"/>
 Adicionar Comissão
 </ModernButton>
{canMarkAsPaid && !isSentToPreparation && (
 <ModernButton
 disabled={Boolean(sale.commissionsConfirmedAt) || isCommissionsBackendBusy}
 onClick={() => void handleConfirmCommissionsStep()}
 >
 {isConfirmingCommissionsStep ? (
 <Loader2 className="h-4 w-4 mr-1.5 animate-spin" aria-hidden />
 ) : null}
 {isConfirmingCommissionsStep
 ? t("actions.loading")
 : t("financialSales.commissions.confirmNoCommissionsCta")}
 </ModernButton>
 )}
 </div>
 {!sale.commissionsConfirmedAt && (
 <p className="mt-4 text-xs text-muted-foreground max-w-md mx-auto">
 {t("financialSales.commissions.stepConfirmHint")}
 </p>
 )}
 {sale.commissionsConfirmedAt && (
 <p className="mt-4 text-sm text-muted-foreground">
 {t("financialSales.commissions.stepConfirmedHint", {
 date: formatDateToBR(sale.commissionsConfirmedAt),
 })}
 </p>
 )}
 </CardContent>
 </Card>
 )}

 <ConfirmDialog
 isOpen={Boolean(commissionIdPendingDelete)}
 title={t("financialSales.commissions.deleteDialogTitle")}
 description={t("financialSales.commissions.deleteDialogDescription")}
 confirmText={t("financialSales.commissions.remove")}
 onClose={() => setCommissionIdPendingDelete(null)}
 onConfirm={() => void handleDeleteCommissionConfirmed()}
 isLoading={isDeletingCommission}
 />

 {/* Add Commission - Inline */}
 {addCommissionDialogOpen && (
 <Card className="border-accent/30">
 <CardHeader className="pb-3">
 <div className="flex items-center justify-between">
 <CardTitle className="text-base">Adicionar Comissao</CardTitle>
 <ModernButton variant="outline" size="sm" className="px-2" onClick={() => { setAddCommissionDialogOpen(false); setSelectedRuleId(""); }} disabled={isCreatingCommission}>x</ModernButton>
 </div>
 </CardHeader>
 <CardContent>
 <div className="space-y-4">
 <div>
 <Label>Regra de Comissao *</Label>
 <Select value={selectedRuleId} onValueChange={setSelectedRuleId} disabled={isCreatingCommission}>
 <SelectTrigger><SelectValue placeholder="Selecione uma regra de comissao"/></SelectTrigger>
 <SelectContent>
 {availableRules.length === 0 ? (
 <SelectItem value="none" disabled>
 {t("financialSales.commissionRuleEvaluation.noRulesInSelect")}
 </SelectItem>
 ) : (
 availableRules.map((rule) => (
 <SelectItem key={rule.id} value={rule.id}>{rule.name}{rule.description && ` - ${rule.description}`}</SelectItem>
 ))
 )}
 </SelectContent>
 </Select>
 {availableRules.length === 0 && <p className="text-sm text-muted-foreground mt-2">Nenhuma regra de comissao aplicavel encontrada para esta venda.</p>}
 </div>
 <div className="flex justify-end gap-2">
 <ModernButton variant="outline"onClick={() => { setAddCommissionDialogOpen(false); setSelectedRuleId(""); }} disabled={isCreatingCommission}>Cancelar</ModernButton>
 <ModernButton onClick={() => void handleAddCommission()} disabled={!selectedRuleId || isCreatingCommission || availableRules.length === 0}>
 {isCreatingCommission ? (
 <Loader2 className="h-4 w-4 mr-1.5 animate-spin" aria-hidden />
 ) : null}
 {isCreatingCommission ? t("actions.loading") : "Adicionar"}
 </ModernButton>
 </div>
 </div>
 </CardContent>
 </Card>
 )}
 </TabsContent>

 <TabsContent value="payments"className="space-y-4 mt-4">
 <SalePaymentsTabContent
 sale={sale}
 onRefresh={async () => { await fetchSale(); }}
 onToast={(message, type) => setToast({ message, type })}
 canEditFinancial={canMarkAsPaid}
 />
 </TabsContent>

 <TabsContent value="documents"className="space-y-4 mt-4">
 {isPostSalesWizard && (
 <SaleOnboardingRequirementsCard
 saleId={sale.id}
 customerId={sale.customerId}
 progress={onboardingProgress}
 saleDocuments={saleDocuments}
 customerDocuments={customerDocuments}
 canEdit={canMutateOnboarding}
 omitContracts={isPostSalesWizard}
 onUpdated={async () => {
 await refreshOnboarding(sale.id);
 await fetchDocuments();
 }}
 onToast={(message, type) => setToast({ message, type })}
 />
 )}
 <Tabs defaultValue="sale-docs"className="w-full">
 <TabsList className="flex flex-wrap h-auto gap-1 py-1">
 <TabsTrigger value="sale-docs">
 {t("financialSales.saleDocumentation.tabSaleDocs")}
 </TabsTrigger>
 <TabsTrigger value="vehicle-docs">
 {t("financialSales.saleDocumentation.tabVehicle")}
 </TabsTrigger>
 {isPostSalesWizard && (
 <TabsTrigger value="contracts">
 {t("financialSales.saleDocumentation.tabContracts")}
 </TabsTrigger>
 )}
 </TabsList>
 <TabsContent value="sale-docs"className="space-y-4 mt-4">
 {displayOnboardingProgress && (
 <SaleDocumentationChecklistSection
 saleId={sale.id}
 saleEntityDocuments={saleDocuments}
 checklist={displayOnboardingProgress.documentation.checklist}
 checklistMeta={displayOnboardingProgress.documentation.checklistMeta}
 canEdit={canMutateOnboarding && sale.status !== "cancelled"}
 onRefresh={async () => {
 await fetchDocuments();
 if (sale.id) void refreshOnboarding(sale.id);
 }}
 onToast={(message, type) => setToast({ message, type })}
 />
 )}
 <Card>
 <CardHeader>
 <CardTitle className="text-base">
 {t("financialSales.saleDocumentation.saleUploadTitle")}
 </CardTitle>
 </CardHeader>
 <CardContent>
 <DocumentUpload
 entityType="sale"
 entityId={sale.id}
 saleId={sale.id}
 onUploadSuccess={async () => {
 await fetchSale();
 await fetchDocuments();
 if (sale.id) void refreshOnboarding(sale.id);
 }}
 />
 </CardContent>
 </Card>
 <DocumentList
 documents={saleDocuments}
 onRemoveDocument={async (docId: string) => {
 try {
 await apiService.deleteEntityDocument(docId);
 fetchDocuments();
 fetchSale();
 if (sale.id) void refreshOnboarding(sale.id);
 } catch (err) {
 console.error("Erro ao remover documento:", err);
 }
 }}
 title={t("financialSales.saleDocumentation.saleListTitle")}
 />
 </TabsContent>
 <TabsContent value="vehicle-docs"className="space-y-4 mt-4">
 <SaleDocumentationVehicleTab
 vehicleId={sale.vehicleId}
 vehicleRegistryDocuments={vehicleRegistryDocuments}
 />
 </TabsContent>
 {isPostSalesWizard && displayOnboardingProgress && (
 <TabsContent value="contracts"className="space-y-4 mt-4">
 <SalePostSalesContractsTab
 saleId={sale.id}
 contracts={displayOnboardingProgress.documentation.contracts}
 saleDocuments={saleDocuments}
 canEdit={canMutateOnboarding}
 onUpdated={async () => {
 await refreshOnboarding(sale.id);
 await fetchDocuments();
 }}
 onToast={(message, type) => setToast({ message, type })}
 />
 </TabsContent>
 )}
 </Tabs>
 </TabsContent>
 </Tabs>
 {isPostSalesWizard && displayOnboardingProgress && sale.status !=="cancelled" && (
 <div className="flex flex-col-reverse sm:flex-row gap-3 justify-between items-stretch sm:items-center pt-6 mt-6 border-t border-border">
 <ModernButton
 type="button"
 variant="outline"
 disabled={wizardStepPersisting !== "idle"}
 onClick={() => {
 if (wizardStepIndex <= 0) navigate("/post-sales/pipeline");
 else void persistPostSalesWizardStep(wizardStepIndex - 1);
 }}
 >
 {wizardStepPersisting === "backward" ? (
 <>
 <Loader2 className="h-4 w-4 mr-2 animate-spin shrink-0" />
 {t("actions.loading")}
 </>
 ) : wizardStepIndex <= 0 ? (
 t("postSalesPipeline.wizard.backToList")
 ) : (
 t("postSalesPipeline.wizard.stepBack")
 )}
 </ModernButton>
 <ModernButton
 type="button"
 className="inline-flex items-center justify-center"
 disabled={
 wizardStepPersisting !== "idle" ||
 !wizardCanAdvance ||
 (wizardStepIndex >= 4 && (sendingPipeline || !canMutateOnboarding))
 }
 onClick={() => {
 if (wizardStepIndex >= 4) void handleSendToPreparationPipeline();
 else void persistPostSalesWizardStep(Math.min(4, wizardStepIndex + 1));
 }}
 >
 {wizardStepIndex >= 4 && sendingPipeline ? (
 <>
 <Loader2 className="h-4 w-4 mr-2 animate-spin shrink-0" />
 {t("financialSales.saleOnboarding.sending")}
 </>
 ) : wizardStepIndex >= 4 ? (
 t("financialSales.saleOnboarding.sendCta")
 ) : wizardStepPersisting === "forward" ? (
 <>
 <Loader2 className="h-4 w-4 mr-2 animate-spin shrink-0" />
 {t("actions.loading")}
 </>
 ) : (
 t("postSalesPipeline.wizard.nextPhase")
 )}
 </ModernButton>
 </div>
 )}
 </CardContent>
 </Card>

 {toast && (
 <Toast
 message={toast.message}
 type={toast.type}
 onClose={() => setToast(null)}
 duration={3000}
 />
 )}
 </div>
 );
};
